import ColumnAssignmentModal from 'common/components/dataUpload/ColumnAssignmentModal'
import DataUploadReport from 'common/components/dataUpload/DataUploadReport'
import NewPeopleModal, {type NewPeopleAnswer} from 'common/components/dataUpload/NewPeopleModal'
import SheetAssignmentModal from 'common/components/dataUpload/SheetAssignmentModal'
import {
  dispatchApi,
  isChunkedUploadAvailable,
  withUploadedFiles,
} from 'common/helpers/chunkedUpload'
import {
  ImportReadiness,
  extractImportReadiness,
  isInstituteExcelUploadResponse,
} from 'common/helpers/csvImportReadiness'
import {
  type DataUploadAllocationAssignment,
  type DataUploadAllocationQuestion,
  type DataUploadAssignmentQuestion,
  type DataUploadBranchAssignments,
  type DataUploadBranchQuestion,
  type DataUploadColumnAssignments,
  type DataUploadConversion,
  type DataUploadDraftAnswers,
  type DataUploadNewStudent,
  type DataUploadNewTeacher,
  type DataUploadSheetAssignments,
  allocationAnswerKey,
  appendAllocationAssignments,
  appendApprovedNewTeachers,
  appendBranchAssignments,
  appendColumnAssignments,
  appendSheetAssignments,
  dataUploadErrorCode,
  dataUploadErrorConversion,
  dataUploadErrorMessage,
  dataUploadErrorQuestions,
  dataUploadErrorStatus,
  dataUploadFileExtension,
  isSpreadsheetUpload,
  normalizeCarriedDrafts,
  normalizeDataUploadConversion,
  normalizeDataUploadDraftAnswers,
  normalizeNewStudents,
  normalizeNewTeachers,
  singleBranchAnswers,
  skippedAllocationAnswers,
  withoutAnsweredAllocations,
  withoutAnsweredBranches,
} from 'common/helpers/dataUpload'
import {ArrowLeft, ArrowRight, Upload, X} from 'lucide-react'
import React, {FC, ReactNode, useCallback, useEffect, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {toast} from 'react-toastify'
import {
  bulkCsvImport,
  confirmCsvImport,
  getCsvImportTableSchemas,
  uploadCsvImportFile,
} from 'setup/redux/Actions/CsvImport/csvImport'
import {getDataUploadDraft} from 'setup/redux/Actions/DataUpload/dataUpload'

import CsvColumnMapper from '../../../DataImport/CsvImport/components/CsvColumnMapper'
import CsvImportReview from '../../../DataImport/CsvImport/components/CsvImportReview'
import ImportReadinessPanel from '../../../DataImport/CsvImport/components/ImportReadinessPanel'
import ImportResultModal from '../../../DataImport/CsvImport/components/ImportResultModal'
import {saveDraftAnswers} from '../hooks/useOnboardingPrefillDraft'
import {STEP_TITLE_KEYS} from '../utils/stepTitles'
import UploadStatusPanel, {type UploadStatusBusy, type UploadStatusItem} from './UploadStatusPanel'

// Mirrors the Excel Import module's own mapping restriction list (CsvImporter.tsx).
const RESTRICTED_TABLES = [
  'timetable_preferences',
  'student_subject',
  'subject',
  'user_teacher',
  'user_student',
  'class_room',
  'class',
  'sections',
  'timetable_subcategories',
  'timetable_categories',
]

interface TableMappings {
  [table: string]: {[key: string]: string}
}

interface PreviewData {
  [key: string]: string
}

/** A finished import: the file and the bulk-import answer, kept by the step (see Props). */
export interface AllocationImportDone {
  file: File
  result: any
  /** Rows the import refused. Only then does the file's line stay, with "View details". */
  failed: number
}

interface Props {
  /** The file picked (or the Teachers step's prefill); upload starts when it arrives. */
  file: File | null
  isDark: boolean
  /** Drop the current file (the X on its line, or a question the user cancelled). */
  onClose: () => void
  /**
   * The import went through. The step keeps `done` and hands it back as `lastImport`: its
   * refresh re-hydrates the wizard, which remounts this tab, so state kept here would be lost.
   */
  onImported: (done: AllocationImportDone) => void
  /** Shown on the file's line, e.g. when the file is the Teachers step's prefill, not a pick. */
  notice?: string
  lastImport: AllocationImportDone | null
  onClearLastImport: () => void
  /**
   * Create these teachers with the wizard's normal teacher save (bulk-import), so the file's
   * lines find them when it is posted again. Rejects when the save fails (it has said why).
   */
  onSaveNewTeachers: (teachers: DataUploadNewTeacher[]) => Promise<void>
  /**
   * The file's new students (`newPeople.students`, another list in the same workbook), handed
   * over once its allocations are done here — imported, or nothing left to import — so the step
   * asks about them after, never before. A students-only file hands them over at once.
   */
  onNewStudents: (
    file: File,
    students: DataUploadNewStudent[],
    /** The Students step's carried draft of them (`carried.students`), when the read kept one. */
    carriedDraftId?: string | null
  ) => void
  /** Registration numbers (lower-cased) of the students already in the Students grid. */
  knownStudentNumbers: ReadonlySet<string>
  /**
   * The school's setup has a Students step (subject-wise schools). Class-wise schools don't:
   * a students list is then neither sent there nor offered to save.
   */
  hasStudentsStep: boolean
  /**
   * The other files picked with `file`: sent in the same request and read as one upload (a
   * teacher listed in one and placed in another is not asked about). One line each on the card.
   */
  extraFiles?: readonly File[]
  /**
   * Redesigned Teacher Allocation page: Map columns opens on the page under the file's line (in
   * the page's own cards and buttons) instead of a dialog, and a single branch is picked without
   * a "Branch Selection Required" card. Left out, the earlier dialog is used.
   */
  inlineMapper?: boolean
}

type Phase = 'uploading' | 'sheets' | 'map' | 'review' | 'failed'

/** Created / updated / failed counts of a bulk-import answer (as ImportResultModal reads them). */
const importCounts = (result: any) => {
  const total = (tables: unknown) =>
    Object.values((tables as Record<string, unknown[]>) || {}).reduce<number>(
      (sum, rows) => sum + (Array.isArray(rows) ? rows.length : 0),
      0
    )
  const message = String(result?.message ?? '')
  const fromMessage = (pattern: RegExp) => {
    const match = message.match(pattern)
    return match ? parseInt(match[1], 10) : null
  }
  return {
    created: fromMessage(/created (\d+)/i) ?? total(result?.created),
    updated: fromMessage(/updated (\d+)/i) ?? total(result?.updated),
    errors:
      fromMessage(/error[s]? (\d+)/i) ?? (Array.isArray(result?.errors) ? result.errors.length : 0),
  }
}

/** The answers given for a file's questions, sent with every re-post of it. */
interface UploadAnswers {
  sheets?: DataUploadSheetAssignments
  branches?: DataUploadBranchAssignments
  columns?: DataUploadColumnAssignments
  /** Which classes each teacher + subject is taught in (`allocationAssignments`). */
  allocations?: DataUploadAllocationAssignment[]
  /** `newPeople.teachers` keys the user said to save (`approvedNewTeachers`). */
  approvedNewTeachers?: string[]
  /** Keys the user left unticked or cancelled: not asked about again for this file (not sent). */
  declinedNewTeachers?: string[]
  /** The file's new students were answered for: not asked about again for this file. */
  studentsDecided?: boolean
}

/** The new-teachers dialog when it has nobody to ask about: one list, not a new one per render. */
const NO_NEW_TEACHERS: DataUploadNewTeacher[] = []

/** The new-students dialog when it has nobody to show: one list, not a new one per render. */
const NO_NEW_STUDENTS: DataUploadNewStudent[] = []

/**
 * Per file, kept outside the card: it unmounts and remounts (the tab reopened, the Teachers-file
 * check hiding it a moment), and must not read the same file twice or forget what was answered.
 * `reads`: each `csv-import/upload` of the file, by what went with it — a remount shows that read
 * (or waits for it) instead of posting again. Dropped once the file is imported.
 */
const allocationReads = new WeakMap<File, Map<string, Promise<any>>>()
/** Every answer given for a file (see UploadAnswers), across remounts. */
const allocationAnswers = new WeakMap<File, UploadAnswers>()
/** Carried files whose draft was asked for the answers kept on it (once per file). */
const allocationAnswersLookedUp = new WeakSet<File>()

const hasKeys = (value: object | undefined | null) => Boolean(value) && Object.keys(value!).length > 0

/** The answers the server reads, as a draft keeps them (only the ones that hold something). */
const toDraftAnswers = (answers: UploadAnswers): DataUploadDraftAnswers => ({
  ...(hasKeys(answers.sheets) ? {sheetAssignments: answers.sheets} : {}),
  ...(hasKeys(answers.columns) ? {columnAssignments: answers.columns} : {}),
  ...(hasKeys(answers.branches) ? {branchAssignments: answers.branches} : {}),
  ...((answers.allocations?.length ?? 0) > 0
    ? {allocationAssignments: answers.allocations}
    : {}),
})

/** A draft's stored answers, as this card keeps them. */
const fromDraftAnswers = (answers: DataUploadDraftAnswers): UploadAnswers => ({
  sheets: answers.sheetAssignments,
  columns: answers.columnAssignments,
  branches: answers.branchAssignments,
  allocations: answers.allocationAssignments,
})

/** The other files read with a file (see `extraFiles`), by that file. */
const allocationCompanions = new WeakMap<File, readonly File[]>()
/** No other files: one list, not a new one per render. */
const NO_FILES: readonly File[] = []

/**
 * A spreadsheet in another layout leaves the user something to decide in Map columns: a column
 * that matched nothing, a match that is not certain, required data the file lacks (branch, level…
 * reported as warnings), or nothing matched at all. Otherwise the columns read cleanly and the
 * file can import from its line.
 */
export const mappingNeedsUser = (
  mappings: Record<string, string>,
  matches: Record<string, {confidence?: string} | undefined> | null | undefined,
  warnings: unknown[] | null | undefined
): boolean => {
  const columns = Object.keys(mappings)
  if (columns.length === 0) return true
  if (columns.some((column) => !mappings[column])) return true
  if (
    Object.values(matches ?? {}).some((match) => match?.confidence && match.confidence !== 'high')
  ) {
    return true
  }
  return (warnings?.length ?? 0) > 0
}

/** A file's new students from its last read, held until its allocations are done. */
const allocationHeldStudents = new WeakMap<File, DataUploadNewStudent[]>()
/** The Students step's carried draft of a file's students (`carried.students.draftId`). */
const allocationCarriedStudents = new WeakMap<File, string>()
/**
 * A file whose allocations were already read on the Students step (`carried.allocation`): it is
 * opened here from that draft (`draftId`, no file), not read again.
 */
const allocationCarriedDrafts = new WeakMap<File, string>()

/** Read `file` on this step from the Students step's carried draft `draftId`. */
export const openCarriedAllocation = (file: File, draftId: string) => {
  allocationCarriedDrafts.set(file, draftId)
}

/** The carried draft `file` is read from, if any (closed once it is imported). */
export const carriedAllocationDraftOf = (file: File): string | null =>
  allocationCarriedDrafts.get(file) ?? null

/** The questions dialog when no branch is asked about: one list, not a new one per render. */
const NO_BRANCH_QUESTIONS: DataUploadBranchQuestion[] = []

/** The questions dialog when no class is asked about: one list, not a new one per render. */
const NO_ALLOCATION_QUESTIONS: DataUploadAllocationQuestion[] = []

/**
 * The Teacher Allocation step's file upload, shown under its buttons like the Add Teachers flow
 * (UploadStatusPanel): the file shows Uploading, Reading, then its summary; the Map columns
 * screen opens as before and imports from there (no separate review or result dialog). No modal for the usual file: our template, or (on the AI servers)
 * any file the server converts into it; the server maps it and returns the bulk payload. Only a
 * plain spreadsheet in another layout still opens the column mapper (map -> review -> import),
 * since its columns must be matched by hand. The questions dialogs (whose list, which branch)
 * stay dialogs, as on the other steps.
 *
 * Underneath it is the Excel Import module's upload -> map columns -> review -> import flow. Hits the exact same APIs as
 * the module (csv-import/upload, csv-import/confirm, csv-import/bulk); only the chrome
 * (routes, stats, walkthrough) is left behind. See CsvImporter.tsx for the original.
 *
 * On the AI servers the file may also be a PDF, Word file, photo or scan: `csv-import/upload`
 * (with `source=onboarding`) converts it into the Teacher Allocation template first, then runs
 * the normal flow. The response is today's response plus a `conversion` field, shown here as a
 * report. Only existing class-subject slots are filled, only with teachers who already exist;
 * rows naming anything else are listed as left out, never created.
 */
// Injected server-side by excelBulkImportBuilder when the workbook names no level. Not a
// level the user ever typed, so it never counts as content.
const FABRICATED_LEVEL_NAME = 'Default Level'

/**
 * True when the server-built bulk payload carries nothing worth importing.
 *
 * An EMPTY Teacher Allocation template still comes back as a "successful" upload: the
 * onboarding upload path skips the completeness gate (`canProceed` is forced true) and the
 * payload builder fabricates a `Default Level` branch entry whenever the file names no
 * level. Posting that payload to bulk-import creates a real "Default Level" in the tenant,
 * which then surfaces on the Curriculum/Levels screens after the post-import re-hydration.
 * Blocking it here keeps the empty payload from ever reaching the API. The backend-side
 * fix is specced in docs/backend-specs/onboarding-empty-teacher-allocation-default-level.md.
 */
const isEmptyBulkImportPayload = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') return true
  const p = payload as Record<string, unknown>
  const rows = (key: string) => (Array.isArray(p[key]) ? (p[key] as unknown[]).length : 0)
  if (rows('teachers') + rows('classes') + rows('subjects') + rows('students') > 0) return false
  const branches = Array.isArray(p.branch) ? (p.branch as Array<Record<string, unknown>>) : []
  const hasRealLevel = branches.some((b) =>
    (Array.isArray(b?.levels) ? (b.levels as Array<Record<string, unknown>>) : []).some(
      (lvl) => String(lvl?.name ?? '').trim() !== FABRICATED_LEVEL_NAME
    )
  )
  return !hasRealLevel
}

const AllocationFileImport: FC<Props> = ({
  file,
  isDark,
  onClose,
  onImported,
  notice,
  lastImport,
  onClearLastImport,
  onSaveNewTeachers,
  onNewStudents,
  knownStudentNumbers,
  hasStudentsStep,
  extraFiles = NO_FILES,
  inlineMapper = false,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()

  const [phase, setPhase] = useState<Phase>('uploading')
  const [importing, setImporting] = useState(false)

  // Upload-response state (same shape the Excel Import module keeps).
  const [preview, setPreview] = useState<PreviewData[]>([])
  const [tableMappings, setTableMappings] = useState<TableMappings>({})
  const [columnMatches, setColumnMatches] = useState<{[key: string]: any}>({})
  const [csvColumns, setCsvColumns] = useState<string[]>([])
  const [columnMappings, setColumnMappings] = useState<{[csvColumn: string]: string}>({})
  const [warnings, setWarnings] = useState<string[]>([])
  const [foreignKeyOptions, setForeignKeyOptions] = useState<{[key: string]: any[]}>({})
  const [spreadsheetTabs, setSpreadsheetTabs] = useState<string[]>([])
  const [selectedTab, setSelectedTab] = useState<string | null>(null)
  const [selectedTable, setSelectedTable] = useState<string | null>(null)
  const [schemas, setSchemas] = useState<{[table: string]: string[]}>({})
  const [defaultValues, setDefaultValues] = useState<{[key: string]: any}>({})
  const [hasUnresolvedColumns, setHasUnresolvedColumns] = useState(false)

  // Institute-template flow: the backend maps the template server-side and returns a
  // ready-to-send bulk payload + readiness verdict instead of per-column matches.
  const [isInstituteExcelFlow, setIsInstituteExcelFlow] = useState(false)
  const [importReadiness, setImportReadiness] = useState<ImportReadiness | null>(null)
  const [bulkImportPayload, setBulkImportPayload] = useState<any>(null)
  /**
   * Nothing imports until these new teachers are saved (the readiness verdict says so). Kept
   * after "Don't save" so the file's line can ask about them again.
   */
  const [blockingTeachers, setBlockingTeachers] = useState<{
    teachers: DataUploadNewTeacher[]
    sheet?: string
  } | null>(null)

  /** The column mapper, for a spreadsheet in another layout only (not our template). */
  const [mapperOpen, setMapperOpen] = useState(false)
  /** The last import's full result (errors, missing references), on request. */
  const [detailsOpen, setDetailsOpen] = useState(false)

  /** Set when the server converted the file first (`result.conversion`). */
  const [conversion, setConversion] = useState<DataUploadConversion | null>(null)
  /**
   * A conversion that produced no usable rows (422 NO_ROWS_CONVERTED): the modal stays open
   * on its report, which says why each row was left out, instead of closing on a toast.
   */
  const [conversionError, setConversionError] = useState<string | null>(null)
  /** 0..1 while the file's pieces are being sent (AI servers); null otherwise. */
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  /**
   * NEEDS_ASSIGNMENT: the workbook holds several unlabelled lists, so nothing was read. Asked
   * with the same dialog as the Teachers and Students steps; the file is posted again with the
   * answer. `sheet` is the spreadsheet tab picked earlier, re-sent as it was.
   */
  const [sheetQuestion, setSheetQuestion] = useState<{
    file: File
    question: DataUploadAssignmentQuestion
    sheet?: string
  } | null>(null)
  /**
   * BRANCH_REQUIRED: the file does not say which branch its allocations are in and its classes
   * exist in several. ALLOCATION_CLASSES_REQUIRED: some lines name no class, or a class with
   * several sections (still unclear inside one branch). Asked with the questions dialog the
   * other steps use (branch cards, "which classes?" cards); then re-posted with the answers.
   */
  const [branchQuestion, setBranchQuestion] = useState<{
    file: File
    questions: DataUploadBranchQuestion[]
    allocations: DataUploadAllocationQuestion[]
    /** New teachers the asked lines name (`conversion.newPeople.teachers`), with Save ticks. */
    teachers: DataUploadNewTeacher[]
    sheet?: string
  } | null>(null)
  /**
   * Every answer given for a file (whose list, which branch, which field). They go with every
   * re-post of that file, keyed by the `File` itself. A file carried from the Students step also
   * keeps them on its draft (`PUT data-upload/drafts/:id/answers`): offered again after a logout,
   * it is not asked again.
   */
  const answersRef = useRef(allocationAnswers)
  const rememberAnswers = (target: File, answers: UploadAnswers) => {
    const previous = answersRef.current.get(target) ?? {}
    const next: UploadAnswers = {
      sheets: {...(previous.sheets ?? {}), ...(answers.sheets ?? {})},
      branches: {...(previous.branches ?? {}), ...(answers.branches ?? {})},
      columns: {...(previous.columns ?? {}), ...(answers.columns ?? {})},
      // One answer per teacher + subject; a later one replaces an earlier one.
      allocations: Array.from(
        new Map(
          [...(previous.allocations ?? []), ...(answers.allocations ?? [])].map((answer) => [
            allocationAnswerKey(answer),
            answer,
          ])
        ).values()
      ),
      approvedNewTeachers: Array.from(
        new Set([...(previous.approvedNewTeachers ?? []), ...(answers.approvedNewTeachers ?? [])])
      ),
      declinedNewTeachers: Array.from(
        new Set([...(previous.declinedNewTeachers ?? []), ...(answers.declinedNewTeachers ?? [])])
      ),
      studentsDecided: Boolean(previous.studentsDecided || answers.studentsDecided),
    }
    answersRef.current.set(target, next)
    const carriedDraft = allocationCarriedDrafts.get(target)
    const serverAnswered =
      answers.sheets || answers.columns || answers.branches || answers.allocations
    if (carriedDraft && serverAnswered) saveDraftAnswers(dispatch, carriedDraft, toDraftAnswers(next))
  }
  /** The grid's registration numbers as they are now (read from upload callbacks). */
  const knownStudentsRef = useRef(knownStudentNumbers)
  const hasStudentsStepRef = useRef(hasStudentsStep)
  const onNewStudentsRef = useRef(onNewStudents)
  /** `onClose` as of the last render, for the upload callback (memoized on first render). */
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    // Declared before the upload effect below, so the files are known when it posts.
    if (file) allocationCompanions.set(file, extraFiles)
    onCloseRef.current = onClose
    knownStudentsRef.current = knownStudentNumbers
    hasStudentsStepRef.current = hasStudentsStep
    onNewStudentsRef.current = onNewStudents
  })
  /** A file's new students from its last read, held until its allocations are done. */
  const pendingStudentsRef = useRef(allocationHeldStudents)
  /** The file's allocations are done: its held students go to the step (once). */
  const releaseStudents = (target: File) => {
    const students = pendingStudentsRef.current.get(target)
    pendingStudentsRef.current.delete(target)
    if (!students || students.length === 0) return
    rememberAnswers(target, {studentsDecided: true})
    onNewStudentsRef.current(target, students, allocationCarriedStudents.get(target) ?? null)
  }
  /** An answer's `carried.students`: kept with the file, for its students popup. */
  const rememberCarried = (target: File, carried: unknown) => {
    const draftId = normalizeCarriedDrafts(carried)?.students?.draftId
    if (draftId) allocationCarriedStudents.set(target, draftId)
  }
  /**
   * The new students of an answer's `newPeople` (one or more places), once each: not those
   * already in the Students grid (same registration number), and none once the user answered
   * for this file.
   */
  const newStudentsFor = (target: File, ...sources: unknown[]): DataUploadNewStudent[] => {
    // No Students step (a class-wise school): students are not part of its setup.
    if (!hasStudentsStepRef.current) return []
    if (answersRef.current.get(target)?.studentsDecided) return []
    const seen = new Set<string>()
    return sources.flatMap(normalizeNewStudents).filter((student) => {
      const number = (student.registrationNumber ?? '').trim().toLowerCase()
      if (number && knownStudentsRef.current.has(number)) return false
      const key =
        number || [student.name.toLowerCase(), student.class ?? '', student.section ?? ''].join('|')
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }
  /**
   * The people the file names that the school has not saved, asked about in one dialog.
   * Teachers: its lines name them. `canContinueWithout`: the rest of the file imports without
   * them. "Save and assign" creates the ticked teachers first (the wizard's teacher save, with
   * their Teacher ID), then posts the same file again — with `approvedNewTeachers` — so their
   * lines match. The file's students are not asked here: they wait until its allocations are
   * done (see `releaseStudents`).
   */
  const [newPeopleQuestion, setNewPeopleQuestion] = useState<{
    file: File
    sheet?: string
    teachers: DataUploadNewTeacher[]
    canContinueWithout: boolean
  } | null>(null)
  const [savingNewTeachers, setSavingNewTeachers] = useState(false)

  const schemasFetchedRef = useRef(false)

  const textColor = isDark ? '#ffffff' : '#181C32'
  const mutedColor = isDark ? '#92929f' : '#7E8299'

  const resetState = useCallback(() => {
    setPhase('uploading')
    setImporting(false)
    setPreview([])
    setTableMappings({})
    setColumnMatches({})
    setCsvColumns([])
    setColumnMappings({})
    setWarnings([])
    setForeignKeyOptions({})
    setSpreadsheetTabs([])
    setSelectedTab(null)
    setSelectedTable(null)
    setDefaultValues({})
    setHasUnresolvedColumns(false)
    setIsInstituteExcelFlow(false)
    setImportReadiness(null)
    setBulkImportPayload(null)
    setBlockingTeachers(null)
    setConversion(null)
    setConversionError(null)
    setUploadProgress(null)
    setMapperOpen(false)
  }, [])

  // Same rename rule as the Excel Import module: with sections but no grades, the
  // backend's `class` mapping actually targets grades.
  const transformTableMappings = (mappings: TableMappings): TableMappings => {
    if (!mappings) return {}
    if (mappings.sections && mappings.class && !mappings.grades) {
      const {class: classMapping, ...rest} = mappings
      return {...rest, grades: classMapping}
    }
    return mappings
  }

  const uploadFile = useCallback(
    (selectedFile: File, sheet?: string) => {
      setPhase('uploading')
      const carriedDraft = allocationCarriedDrafts.get(selectedFile) ?? null
      // A carried draft opened with no answers here (after a logout): the ones given before are
      // on the draft. Asked once, then read with them; any failure reads it without.
      if (
        carriedDraft &&
        !answersRef.current.has(selectedFile) &&
        !allocationAnswersLookedUp.has(selectedFile)
      ) {
        allocationAnswersLookedUp.add(selectedFile)
        dispatchApi(dispatch, (ok, fail) => getDataUploadDraft(carriedDraft, ok, fail))
          .then((res: any) => {
            const draft = (res?.result ?? res)?.draft
            const stored = normalizeDataUploadDraftAnswers(draft?.answers)
            if (stored && !answersRef.current.has(selectedFile)) {
              answersRef.current.set(selectedFile, fromDraftAnswers(stored))
            }
          })
          .catch(() => undefined)
          .finally(() => uploadFile(selectedFile, sheet))
        return
      }
      // Every file picked with it goes in the same request, read as one upload.
      const together: readonly File[] = carriedDraft
        ? [selectedFile]
        : [selectedFile, ...(allocationCompanions.get(selectedFile) ?? [])]
      /** The file itself, or (sent in pieces first) its `uploadId`. */
      const request = (uploadIds: string[] | null) => {
        const formData = new FormData()
        // Read on the Students step already: finished from that draft, no file sent.
        if (carriedDraft) formData.append('draftId', carriedDraft)
        else if (uploadIds && uploadIds.length > 1) {
          formData.append('uploadIds', JSON.stringify(uploadIds))
        } else if (uploadIds?.length === 1) formData.append('uploadId', uploadIds[0])
        else if (together.length > 1) together.forEach((one) => formData.append('files', one))
        else formData.append('file', selectedFile)
        if (sheet) formData.append('sheet', sheet)
        // Earlier answers for this file (whose list, which branch, which field): the server
        // keeps none, so every re-post carries them.
        const answers = answersRef.current.get(selectedFile)
        appendSheetAssignments(formData, answers?.sheets)
        appendBranchAssignments(formData, answers?.branches)
        appendColumnAssignments(formData, answers?.columns)
        appendAllocationAssignments(formData, answers?.allocations)
        // The new teachers the user said to save: the server creates them and keeps their lines.
        appendApprovedNewTeachers(formData, answers?.approvedNewTeachers)
        // Onboarding uploads skip the strict completeness validation server-side;
        // the sidebar Excel Import module (no source flag) keeps it.
        formData.append('source', 'onboarding')
        // The server defaults to `allocation` for onboarding, but older builds (and the sidebar
        // Excel Import) need it explicit. Our own template is still passed through untouched.
        // Legacy .xls isn't a data-upload format.
        if (dataUploadFileExtension(selectedFile.name) !== 'xls') {
          formData.append('useCaseKey', 'allocation')
        }
        return dispatchApi(dispatch, (ok, fail) => uploadCsvImportFile(formData, ok, fail))
      }
      // nginx refuses bodies over 1 MB on the AI servers: the file goes in 512 KB pieces there
      // (a picked sheet again reuses the same upload). Legacy .xls isn't a data-upload format.
      const chunked =
        !carriedDraft &&
        isChunkedUploadAvailable() &&
        together.every((one) => dataUploadFileExtension(one.name) !== 'xls')
      // One read per file and answers: a read already made (or on its way) is reused.
      const reads = allocationReads.get(selectedFile) ?? new Map<string, Promise<any>>()
      allocationReads.set(selectedFile, reads)
      // What the server is sent (not the answers kept here only, like declined teachers).
      const sent = answersRef.current.get(selectedFile)
      const readKey = JSON.stringify([
        together.map((one) => one.name),
        sheet ?? null,
        sent?.sheets ?? {},
        sent?.branches ?? {},
        sent?.columns ?? {},
        sent?.allocations ?? [],
        sent?.approvedNewTeachers ?? [],
      ])
      const pending: Promise<any> =
        reads.get(readKey) ??
        (chunked
          ? withUploadedFiles(
              dispatch,
              together,
              {useCaseKey: 'allocation', onProgress: setUploadProgress},
              (uploadIds) => {
                setUploadProgress(null)
                return request(uploadIds)
              }
            )
          : request(null))
      reads.set(readKey, pending)

      pending.then(
        (response: any) => {
          setUploadProgress(null)
          const responseData = response.result || response.data || response
          if (!(response.statusCode === 200 || response.status === 'true')) {
            setConversionError(
              response.result?.error ||
                response.message ||
                intl.formatMessage({id: 'CSV_IMPORT.ERROR.UPLOAD_FAILED'})
            )
            setPhase('failed')
            return
          }

          const {
            preview: previewData,
            mappings,
            columnMatches: matches,
            sheets,
            warnings: responseWarnings,
            foreignKeyOptions: fkOptions,
          } = responseData

          setConversion(normalizeDataUploadConversion(responseData?.conversion))

          const instituteFlow = isInstituteExcelUploadResponse(responseData)
          setIsInstituteExcelFlow(instituteFlow)
          const serverPayload =
            responseData?.bulkImportPayload ?? responseData?.result?.bulkImportPayload ?? null
          let readiness = extractImportReadiness(responseData)
          let payloadToImport = serverPayload
          // Empty template: block instead of trusting the server's forced `canProceed`. The
          // server says why when it knows (no row matches an existing subject and teacher, no
          // row has a Level…); "no data rows" is only the fallback.
          if (instituteFlow && isEmptyBulkImportPayload(serverPayload)) {
            const serverReason =
              readiness?.canProceed === false ? String(readiness.summaryMessage ?? '').trim() : ''
            readiness = {
              ...(readiness ?? {}),
              canProceed: false,
              summaryMessage:
                serverReason || intl.formatMessage({id: 'CSV_IMPORT.ERROR.EMPTY_FILE'}),
              missingItems: [],
              warningMessage: undefined,
              warnings: [],
            }
            // Dropped, not kept: handleImport must have nothing to send even if a later
            // state change were to flip `blocked` off.
            payloadToImport = null
          }
          setImportReadiness(readiness)
          setBulkImportPayload(payloadToImport)
          // Lines naming teachers the school has not saved: every line (nothing can import yet)
          // or some (the rest can). Either way the user is asked whether to save them.
          // Each teacher is asked about once per file: a re-post still names the ones the user
          // left unticked, and those must not bring the dialog back.
          const given = answersRef.current.get(selectedFile)
          const answered = new Set([
            ...(given?.approvedNewTeachers ?? []),
            ...(given?.declinedNewTeachers ?? []),
          ])
          // Two places: our template's own lines (`newPeople`), and a converted file's lines,
          // which the conversion already dropped for their teacher (`conversion.newPeople`).
          const reported = new Map<string, DataUploadNewTeacher>()
          ;[
            ...normalizeNewTeachers(responseData?.newPeople),
            ...normalizeNewTeachers(responseData?.conversion?.newPeople),
          ].forEach((teacher) => {
            if (!reported.has(teacher.key)) reported.set(teacher.key, teacher)
          })
          const newTeachers = Array.from(reported.values()).filter(
            (teacher) => !answered.has(teacher.key)
          )
          // Blocked because of those teachers: the server's summary names every one of them
          // (fifty names in a paragraph). They are listed in the new-teachers dialog; the card
          // and the readiness panel say it in one line.
          const serverSummary = String(readiness?.summaryMessage ?? '')
          const teacherBlocked =
            readiness?.canProceed === false &&
            Array.from(reported.values()).some(
              (teacher) => teacher.name && serverSummary.includes(teacher.name)
            )
          if (teacherBlocked) {
            readiness = {
              ...(readiness ?? {}),
              summaryMessage: intl.formatMessage({
                id: 'DATA_UPLOAD.CONVERT.ERROR.TEACHERS_NOT_IN_SCHOOL',
              }),
            }
            setImportReadiness(readiness)
          }
          setBlockingTeachers(
            teacherBlocked ? {teachers: Array.from(reported.values()), sheet} : null
          )
          // A workbook with a students sheet too: its students wait until the allocations are
          // imported (the step's own data first, the other steps' after).
          const newStudents = newStudentsFor(
            selectedFile,
            responseData?.newPeople,
            responseData?.conversion?.newPeople
          )
          if (newStudents.length > 0) pendingStudentsRef.current.set(selectedFile, newStudents)
          rememberCarried(selectedFile, responseData?.conversion?.carried)
          // Asked first; the Map columns screen opens once it is answered (see NewPeopleModal).
          const askingNewPeople = newTeachers.length > 0
          if (askingNewPeople) {
            setNewPeopleQuestion({
              file: selectedFile,
              sheet,
              teachers: newTeachers,
              canContinueWithout: readiness?.canProceed !== false,
            })
          }
          setPreview(previewData || [])
          setWarnings(responseWarnings || [])
          setForeignKeyOptions(fkOptions || {})

          if (instituteFlow) {
            // Blocked or auto-mapped: either way the map phase renders the right panel.
            const hasMappingData =
              (mappings && Object.keys(mappings).length > 0) ||
              (matches && Object.keys(matches).length > 0)
            if (!hasMappingData || readiness?.canProceed === false) {
              setPhase('map')
              // Auto-mapped: the Map columns screen shows what it will import. Blocked: it shows
              // why (readiness panel), with Import disabled — as the Excel Import flow always has.
              // On the page (`inlineMapper`) there is nothing to decide here: the server built
              // the payload, and the file's line says what imports or why it can't.
              if (!askingNewPeople && !inlineMapper) setMapperOpen(true)
              return
            }
          }

          if (!instituteFlow && sheets && sheets.length > 0) {
            setSpreadsheetTabs(sheets)
            setPhase('sheets')
            setMapperOpen(true)
            return
          }

          const transformedMappings = transformTableMappings(mappings)
          setTableMappings(transformedMappings)
          setColumnMatches(matches || {})
          if (transformedMappings && Object.keys(transformedMappings).length > 0) {
            setSelectedTable(Object.keys(transformedMappings)[0])
          }

          if (!instituteFlow && (!matches || Object.keys(matches).length === 0)) {
            setConversionError(intl.formatMessage({id: 'CSV_IMPORT.ERROR.NO_COLUMN_MATCHES'}))
            setPhase('failed')
            return
          }

          // Initialize mappings from the matches, enforcing one-to-one like the module
          // (all user_teacher columns are exempt and may repeat).
          const columns = matches ? Object.keys(matches) : []
          setCsvColumns(columns)
          const initialMappings: {[key: string]: string} = {}
          const usedDbColumns = new Set<string>()
          const isUserTeacherColumn = (dbColumnName: string) =>
            !!dbColumnName && dbColumnName.includes('user_teacher')
          const sortedColumns = [...columns].sort(
            (a, b) => (matches?.[b]?.similarity ?? 0) - (matches?.[a]?.similarity ?? 0)
          )
          sortedColumns.forEach((csvCol) => {
            const match = matches?.[csvCol]
            let dbColumnName: string | null = null
            if (match) {
              const column =
                match.dbColumn || match.column || match.databaseColumn || match.targetColumn
              if (column && match.table) dbColumnName = `${match.table}.${column}`
            }
            if (
              dbColumnName &&
              (!usedDbColumns.has(dbColumnName) || isUserTeacherColumn(dbColumnName))
            ) {
              initialMappings[csvCol] = dbColumnName
              if (!isUserTeacherColumn(dbColumnName)) usedDbColumns.add(dbColumnName)
            } else {
              initialMappings[csvCol] = ''
            }
          })
          setColumnMappings(initialMappings)
          setPhase('map')
          // The Map columns screen: how the columns were read, before import. On the page
          // (`inlineMapper`) it opens only when there is something to decide — never for our
          // template / a converted file (the server built the payload), and for another layout
          // only when a column is unmatched or uncertain, or required data is missing.
          const needsUser = instituteFlow
            ? false
            : mappingNeedsUser(initialMappings, matches, responseWarnings)
          if (!askingNewPeople && (!inlineMapper || needsUser)) setMapperOpen(true)
        },
        (error: any) => {
          setUploadProgress(null)
          // A converted upload reports its failure as `result.code` (+ `result.conversion`
          // when rows were read but none matched the school). A chunked upload fails with
          // the same codes, or with no answer at all (a proxy page, a dropped connection).
          const code = dataUploadErrorCode(error)
          rememberCarried(selectedFile, error?.result?.conversion?.carried)
          // Nothing was read yet: a question comes back instead. Ask it, then post the same
          // file again with the answer (the next read may still ask the other one).
          if (
            code === 'NEEDS_ASSIGNMENT' ||
            code === 'BRANCH_REQUIRED' ||
            code === 'ALLOCATION_CLASSES_REQUIRED'
          ) {
            const asked = dataUploadErrorQuestions(error)
            const given = answersRef.current.get(selectedFile)
            const open = withoutAnsweredBranches(asked.branches, given?.branches)
            const allocations = withoutAnsweredAllocations(asked.allocations, given?.allocations)
            if (code === 'NEEDS_ASSIGNMENT' && asked.sheets) {
              setSheetQuestion({file: selectedFile, question: asked.sheets, sheet})
              return
            }
            // The asked lines' teachers the school has not saved come with the same refusal:
            // asked in the same dialog, so the answers and the save go with one re-post (not a
            // later TEACHERS_NOT_IN_SCHOOL). Each is asked about once per file.
            const decided = new Set([
              ...(given?.approvedNewTeachers ?? []),
              ...(given?.declinedNewTeachers ?? []),
            ])
            const teachers = normalizeNewTeachers(error?.result?.conversion?.newPeople).filter(
              (teacher) => !decided.has(teacher.key)
            )
            // One branch to choose from: that one, without asking.
            const single = singleBranchAnswers(open)
            const branches = open.filter((question) => !(question.key in single))
            if (Object.keys(single).length > 0) {
              rememberAnswers(selectedFile, {branches: single})
              if (branches.length === 0 && allocations.length === 0 && teachers.length === 0) {
                uploadFile(selectedFile, sheet)
                return
              }
            }
            if (branches.length > 0 || allocations.length > 0 || teachers.length > 0) {
              setBranchQuestion({
                file: selectedFile,
                questions: branches,
                allocations,
                teachers,
                sheet,
              })
              return
            }
          }
          // A converted file (the school's own layout) whose every line names a new teacher:
          // 422, the teachers in `conversion.newPeople`. Saved the same way: the file is posted
          // again with `approvedNewTeachers`.
          if (code === 'TEACHERS_NOT_IN_SCHOOL') {
            const given = answersRef.current.get(selectedFile)
            const answered = new Set([
              ...(given?.approvedNewTeachers ?? []),
              ...(given?.declinedNewTeachers ?? []),
            ])
            const missing = normalizeNewTeachers(error?.result?.conversion?.newPeople).filter(
              (teacher) => !answered.has(teacher.key)
            )
            const students = newStudentsFor(selectedFile, error?.result?.conversion?.newPeople)
            if (students.length > 0) pendingStudentsRef.current.set(selectedFile, students)
            if (missing.length > 0) {
              setNewPeopleQuestion({
                file: selectedFile,
                sheet,
                teachers: missing,
                canContinueWithout: false,
              })
            }
          }
          const converted = !isSpreadsheetUpload(selectedFile)
          const noAnswer =
            error instanceof Error ||
            [408, 413, 502, 504].includes(dataUploadErrorStatus(error) ?? 0)
          if (code || converted || (chunked && noAnswer)) {
            const failedConversion = dataUploadErrorConversion(error)
            // A students list: offer to save its students (on the Students step) rather than
            // only saying where it belongs. Students already in the grid are not offered.
            const listedStudents =
              code === 'NO_RELEVANT_DATA'
                ? newStudentsFor(selectedFile, error?.result?.conversion?.newPeople)
                : []
            if (listedStudents.length > 0) {
              // Nothing to import here: the card goes, and the step asks about the students.
              pendingStudentsRef.current.set(selectedFile, listedStudents)
              resetState()
              onCloseRef.current()
              releaseStudents(selectedFile)
              return
            }
            // Another screen's list (the file said so): say where to upload it. This step has no
            // prefill for other screens, so nothing was moved.
            const owner = failedConversion?.files.find(
              (f) => f.assignedTo && f.assignedTo !== 'allocation' && STEP_TITLE_KEYS[f.assignedTo]
            )
            if (code === 'NO_RELEVANT_DATA' && owner) {
              setConversion(null)
              setConversionError(
                // A class-wise school has no Students step to send a students list to.
                owner.assignedTo === 'students' && !hasStudentsStepRef.current
                  ? intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.STUDENTS_NOT_IN_SETUP'})
                  : intl.formatMessage(
                      {id: 'DATA_UPLOAD.CONVERT.BELONGS_TO_STEP'},
                      {step: intl.formatMessage({id: STEP_TITLE_KEYS[owner.assignedTo!]})}
                    )
              )
              setPhase('failed')
              return
            }
            const message = dataUploadErrorMessage(intl.formatMessage, error, {
              useCaseKey: 'allocation',
              fileName: selectedFile.name,
            })
            if (failedConversion) {
              setConversion(failedConversion)
              setConversionError(message)
              setPhase('failed')
              return
            }
            setConversionError(message)
            setPhase('failed')
            return
          }
          const errData = error?.response?.data || error?.result || error
          setConversionError(
            errData?.result?.error ||
              errData?.error ||
              errData?.message ||
              error?.message ||
              intl.formatMessage({id: 'CSV_IMPORT.ERROR.UPLOAD_FAILED'})
          )
          setPhase('failed')
        }
      )
    },
    [dispatch, inlineMapper, intl]
  )

  /**
   * The screens a sheet may be given to. csv-import/upload's NEEDS_ASSIGNMENT carries the sheets
   * but not the choices `data-upload/convert` sends beside them: the three screens then.
   */
  const sheetChoices = (question: DataUploadAssignmentQuestion): DataUploadAssignmentQuestion =>
    question.choices.length > 0
      ? question
      : {
          ...question,
          choices: [
            {
              key: 'teachers',
              title: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TABS.TEACHERS'}),
            },
            {
              key: 'students',
              title: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TABS.STUDENTS'}),
            },
            {
              key: 'allocation',
              title: intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TABS.LESSON_PLANNING',
              }),
            },
          ],
        }

  // Kick the upload off whenever a fresh file arrives.
  useEffect(() => {
    if (!file) return
    setSheetQuestion(null)
    setBranchQuestion(null)
    setNewPeopleQuestion(null)
    resetState()
    uploadFile(file)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file])

  // Table schemas feed the mapper's dropdowns; fetched once, filtered like the module.
  useEffect(() => {
    if (!file || schemasFetchedRef.current) return
    schemasFetchedRef.current = true
    dispatch(
      getCsvImportTableSchemas(
        (res: any) => {
          const allSchemas = res?.result?.schemas || res?.result || {}
          const filtered: {[table: string]: string[]} = {}
          Object.entries(allSchemas).forEach(([table, columns]) => {
            if (RESTRICTED_TABLES.includes(table)) {
              filtered[table] = columns as string[]
            }
          })
          setSchemas(filtered)
        },
        () => {}
      ) as never
    )
  }, [file, dispatch])

  const handleImportSuccess = (response: any) => {
    const responseData = response.result || response.data || response
    const isSuccessResponse =
      response?.statusCode === 200 ||
      response?.status === 'true' ||
      response?.status === true ||
      response?.success === 'true' ||
      response?.success === true
    setImporting(false)
    if (!isSuccessResponse) {
      toast.error(response.message || intl.formatMessage({id: 'CSV_IMPORT.ERROR.IMPORT_FAILED'}))
      return
    }
    // Shown on the file's line (with "View details"), not in a result dialog to dismiss.
    const result = {...responseData, message: response.message || responseData.message || ''}
    const imported = file
    // The allocations are in: now the file's students, if it lists any. Its reads are spent (a
    // later read must see what was just imported).
    if (imported) {
      releaseStudents(imported)
      allocationReads.delete(imported)
    }
    resetState()
    onClose()
    if (imported) onImported({file: imported, result, failed: importCounts(result).errors})
  }

  const handleImportFailure = (error: any) => {
    setImporting(false)
    const responseData = error?.result || error?.data || error
    const readiness = extractImportReadiness(responseData) ?? extractImportReadiness(error)
    if (readiness) {
      setImportReadiness(readiness)
      if (readiness.canProceed === false) setPhase('map')
    }
    toast.error(
      readiness?.summaryMessage ||
        error?.message ||
        intl.formatMessage({id: 'CSV_IMPORT.ERROR.IMPORT_FAILED'})
    )
  }

  const handleImport = () => {
    if (!file) return

    if (isInstituteExcelFlow) {
      // Second line of defence for the empty-template case (see isEmptyBulkImportPayload).
      if (isEmptyBulkImportPayload(bulkImportPayload)) {
        toast.error(intl.formatMessage({id: 'CSV_IMPORT.ERROR.EMPTY_FILE'}))
        return
      }
      if (importReadiness?.canProceed === false) {
        toast.error(
          importReadiness.summaryMessage ||
            'Please complete all required data in the Excel file before continuing.'
        )
        return
      }
      setImporting(true)
      dispatch(
        bulkCsvImport(
          // source: 'onboarding' with no checkpoint marks this as the onboarding Excel import,
          // so node-api drops a "Default Level" nothing in the payload uses (backend spec
          // onboarding-empty-teacher-allocation-default-level.md, part C). Do not add checkpoint.
          {...(bulkImportPayload as Record<string, unknown>), source: 'onboarding'},
          handleImportSuccess,
          handleImportFailure
        ) as never
      )
      return
    }

    setImporting(true)

    // Rebuild tableMappings from the current columnMappings so manual changes made in
    // the mapper are reflected in the payload — same construction as the module.
    const finalTableMappings: TableMappings = {}
    Object.entries(columnMappings).forEach(([csvColumn, mapping]) => {
      if (!mapping) return
      const parts = mapping.split('.').filter(Boolean)
      if (parts.length < 2) return
      const dbColumn = parts[parts.length - 1]
      const tableName = parts[parts.length - 2]
      if (!finalTableMappings[tableName]) finalTableMappings[tableName] = {}
      finalTableMappings[tableName][csvColumn] = dbColumn
    })

    if (defaultValues && Object.keys(defaultValues).length > 0) {
      Object.entries(defaultValues).forEach(([column, option]: [string, any]) => {
        let mappingKey: string
        let databaseColumn: string
        let tableName: string
        if (column === 'user_teacher') {
          mappingKey = 'teacher_cnic'
          databaseColumn = 'cnic'
          tableName = 'user_teacher'
        } else if (column === 'grades') {
          mappingKey = 'grades_name'
          databaseColumn = 'name'
          tableName = 'grades'
        } else {
          mappingKey = `${column}_name`
          databaseColumn = 'name'
          tableName = column
        }
        if (!finalTableMappings[tableName]) finalTableMappings[tableName] = {}
        finalTableMappings[tableName][mappingKey] = databaseColumn
        const selectedValue = column === 'user_teacher' ? option.cnic : option.name
        preview.forEach((row) => {
          row[mappingKey] = selectedValue
        })
      })
    }

    const isExcelFile =
      file.name.toLowerCase().endsWith('.xlsx') ||
      file.name.toLowerCase().endsWith('.xls') ||
      spreadsheetTabs.length > 0 ||
      !!selectedTab

    dispatch(
      confirmCsvImport(
        {
          data: preview,
          tableMappings: finalTableMappings,
          columnMappings,
          fileType: isExcelFile ? 'spreadsheet' : 'csv',
          dataType: 'all',
          targetTable: selectedTable,
          sheet: selectedTab,
          studentSubject: 'manual',
        },
        handleImportSuccess,
        handleImportFailure
      ) as never
    )
  }

  const blocked = importReadiness?.canProceed === false
  const showReadinessPanel =
    blocked ||
    Boolean(importReadiness?.warningMessage) ||
    (importReadiness?.warnings?.length ?? 0) > 0
  const autoMapped =
    isInstituteExcelFlow && csvColumns.length === 0 && Boolean(bulkImportPayload) && !blocked

  const isNextDisabled =
    phase !== 'map' || blocked || (!autoMapped && hasUnresolvedColumns) || importing
  const isImportDisabled =
    importing ||
    blocked ||
    (!isInstituteExcelFlow &&
      Object.values(columnMappings).filter((mapping) => mapping !== '').length === 0) ||
    (isInstituteExcelFlow && !bulkImportPayload)

  // The allocation lines bulk-import receives: one per class-subject slot with a teacher.
  const allocationCount = Array.isArray(bulkImportPayload?.subjects)
    ? bulkImportPayload.subjects.length
    : null
  /** Our template (or a converted file), read and mapped: its line imports it in place. */
  const inlineImport = isInstituteExcelFlow && (phase === 'map' || phase === 'review')
  /** A spreadsheet in another layout: its columns are matched in the mapper dialog. */
  const needsMapping = !isInstituteExcelFlow && ['sheets', 'map', 'review'].includes(phase)

  const importLabel =
    allocationCount !== null
      ? intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.IMPORT_ALLOCATIONS'},
          {count: allocationCount}
        )
      : intl.formatMessage({id: 'CSV_IMPORT.BUTTON.IMPORT_DATA'})

  /** Section -> row-count summary for a template the backend already auto-mapped. */
  const renderAutoMappedSummary = () => (
    <div
      style={{
        border: `1px solid ${isDark ? '#2b2b40' : '#e1e3ea'}`,
        borderRadius: 12,
        padding: '1.25rem 1.5rem',
      }}
    >
      <p className='mb-3' style={{color: textColor, fontWeight: 600}}>
        {intl.formatMessage({id: 'CSV_IMPORT.STEPS.MAP_COLUMNS'})}
      </p>
      <div className='d-flex flex-wrap gap-2'>
        {Object.entries(bulkImportPayload || {})
          .filter(([, value]) => Array.isArray(value))
          .map(([section, value]) => (
            <span
              key={section}
              className='badge badge-light-primary'
              style={{fontSize: '0.85rem', padding: '0.55rem 0.8rem'}}
            >
              {section}: {(value as any[]).length}
            </span>
          ))}
      </div>
      <p className='mt-3 mb-0' style={{color: mutedColor, fontSize: '0.9rem'}}>
        {intl.formatMessage({id: 'CSV_IMPORT.INSTRUCTIONS.DOWNLOAD_TEMPLATE_ABOVE'})}
      </p>
    </div>
  )

  /**
   * The new-teachers dialog's answer. The ticked teachers are saved first; then the file is
   * posted again when they were saved to be assigned, or goes on to Map columns, or — nothing
   * left to import here — is dropped (and its students, if any, are asked about).
   */
  const answerNewPeople = (answer: NewPeopleAnswer) => {
    const pending = newPeopleQuestion
    if (!pending) return
    const picked = pending.teachers.filter((teacher) => answer.teacherKeys.includes(teacher.key))
    const approved = answer.teachers === 'assign' ? answer.teacherKeys : []
    setSavingNewTeachers(true)
    ;(picked.length > 0 ? onSaveNewTeachers(picked) : Promise.resolve())
      .then(() => {
        setSavingNewTeachers(false)
        setNewPeopleQuestion(null)
        rememberAnswers(pending.file, {
          approvedNewTeachers: approved,
          // Not asked about again for this file.
          declinedNewTeachers: pending.teachers
            .map((teacher) => teacher.key)
            .filter((key) => !approved.includes(key)),
        })
        if (answer.teachers === 'only' && picked.length > 0) {
          toast.success(
            intl.formatMessage({id: 'DATA_UPLOAD.NEW_TEACHERS.SAVED_ONLY'}, {count: picked.length})
          )
        }
        // They exist now: the re-read matches their lines, then Map columns opens.
        const repost = approved.length > 0
        // Nothing left to import here: every line was a teacher saved without assigning (a
        // re-read would now allocate them, so it is not posted again).
        const finished = answer.teachers === 'only' && !pending.canContinueWithout
        if (repost) uploadFile(pending.file, pending.sheet)
        else if (finished) {
          releaseStudents(pending.file)
          resetState()
          onClose()
        } else if (
          !blockingTeachers &&
          (!inlineMapper ||
            (!isInstituteExcelFlow && mappingNeedsUser(columnMappings, columnMatches, warnings)))
        ) {
          // On to Map columns, as after any read (blocked, it says why there). On the page, only
          // when there is something to decide (see the read above).
          setMapperOpen(true)
        }
        // Blocked by the teachers not saved: Map columns would only repeat that. The file's
        // line says it and offers to save them.
      })
      // A teacher save failed and said why; the dialog stays open to try again.
      .catch(() => setSavingNewTeachers(false))
  }

  const items: UploadStatusItem[] = []
  if (file) {
    const busy = phase === 'uploading' || importing
    items.push({
      id: 'current',
      file,
      busy,
      busyText: importing
        ? intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.IMPORTING'})
        : uploadProgress !== null
          ? intl.formatMessage(
              {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_UPLOADING'},
              {percent: Math.round(uploadProgress * 100)}
            )
          : intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_EXTRACTING',
            }),
      progress: phase === 'uploading' ? uploadProgress : null,
      // While the new-teachers dialog asks, it is the message: nothing is repeated behind it.
      error: newPeopleQuestion
        ? null
        : phase === 'failed'
          ? (conversionError ?? intl.formatMessage({id: 'CSV_IMPORT.ERROR.UPLOAD_FAILED'}))
          : inlineImport && blocked
            ? String(importReadiness?.summaryMessage ?? '').trim() ||
              intl.formatMessage({id: 'CSV_IMPORT.ERROR.EMPTY_FILE'})
            : null,
      // On the page, columns that read cleanly need no matching, so the hint is left out.
      hint:
        needsMapping &&
        !(
          inlineMapper &&
          phase === 'map' &&
          !mappingNeedsUser(columnMappings, columnMatches, warnings)
        )
          ? intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.MATCH_COLUMNS_HINT'})
          : (notice ?? null),
      badges:
        inlineImport && !blocked && allocationCount !== null
          ? [
              {
                tone: 'success',
                text: intl.formatMessage(
                  {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.ALLOCATIONS_READY'},
                  {count: allocationCount}
                ),
              },
            ]
          : [],
      action:
        inlineImport && blocked && blockingTeachers ? (
          newPeopleQuestion ? null : (
            <button
              type='button'
              className='btn btn-sm btn-light-primary text-nowrap'
              disabled={savingNewTeachers}
              onClick={() =>
                setNewPeopleQuestion({
                  file: file,
                  sheet: blockingTeachers.sheet,
                  teachers: blockingTeachers.teachers,
                  canContinueWithout: false,
                })
              }
            >
              {intl.formatMessage(
                {id: 'DATA_UPLOAD.NEW_TEACHERS.SAVE'},
                {count: blockingTeachers.teachers.length}
              )}
            </button>
          )
        ) : inlineImport && blocked && !mapperOpen && !inlineMapper ? (
          // On the page the line's error says why; there is nothing to map.
          <button
            type='button'
            className='btn btn-sm btn-light-primary text-nowrap'
            onClick={() => setMapperOpen(true)}
          >
            {intl.formatMessage({id: 'CSV_IMPORT.STEPS.MAP_COLUMNS'})}
          </button>
        ) : inlineImport && !blocked && !(inlineMapper && mapperOpen) ? (
          // On the page: Import only — the server built the payload, nothing to decide.
          <span className='d-flex flex-wrap gap-2'>
            {!inlineMapper && (
              <button
                type='button'
                className='btn btn-sm btn-light-primary text-nowrap'
                disabled={importing}
                onClick={() => setMapperOpen(true)}
              >
                {intl.formatMessage({id: 'CSV_IMPORT.STEPS.MAP_COLUMNS'})}
              </button>
            )}
            <button
              type='button'
              className='btn btn-sm btn-primary text-nowrap'
              disabled={isImportDisabled}
              onClick={handleImport}
            >
              {importing ? (
                <span className='spinner-border spinner-border-sm me-2' role='status' />
              ) : null}
              {importLabel}
            </button>
          </span>
        ) : needsMapping &&
          !mapperOpen &&
          inlineMapper &&
          phase === 'map' &&
          !mappingNeedsUser(columnMappings, columnMatches, warnings) ? (
          // On the page, columns that read cleanly import from the line: nothing to decide.
          <button
            type='button'
            className='btn btn-sm btn-primary text-nowrap'
            disabled={isImportDisabled}
            onClick={handleImport}
          >
            {importing ? (
              <span className='spinner-border spinner-border-sm me-2' role='status' />
            ) : null}
            {intl.formatMessage({id: 'CSV_IMPORT.BUTTON.IMPORT_DATA'})}
          </button>
        ) : needsMapping && !mapperOpen ? (
          <button
            type='button'
            className='btn btn-sm btn-primary text-nowrap'
            onClick={() => setMapperOpen(true)}
          >
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.MATCH_COLUMNS'})}
          </button>
        ) : null,
    })
  } else if (lastImport) {
    const counts = importCounts(lastImport.result)
    const badges: Array<{text: string; tone: string}> = []
    if (counts.created > 0) {
      badges.push({
        tone: 'success',
        text: intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.IMPORT_ADDED'},
          {count: counts.created}
        ),
      })
    }
    if (counts.updated > 0) {
      badges.push({
        tone: 'primary',
        text: intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.IMPORT_UPDATED'},
          {count: counts.updated}
        ),
      })
    }
    if (counts.errors > 0) {
      badges.push({
        tone: 'danger',
        text: intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.IMPORT_FAILED_ROWS'},
          {count: counts.errors}
        ),
      })
    }
    if (badges.length === 0) {
      badges.push({
        tone: 'success',
        text: intl.formatMessage({id: 'CSV_IMPORT.RESULT.IMPORT_COMPLETED'}),
      })
    }
    // Only an import with refused rows stays on the panel; a clean one leaves it at once.
    items.push({
      id: 'imported',
      file: lastImport.file,
      badges,
      action: (
        <button
          type='button'
          className='btn btn-sm btn-light-primary text-nowrap'
          onClick={() => setDetailsOpen(true)}
        >
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.VIEW_DETAILS'})}
        </button>
      ),
    })
  }

  // One line per file still to read, under the one being read (like the Students card).
  if (file) {
    // The files read with it: a line each, removed with it (they are one upload).
    extraFiles.forEach((extra, index) => {
      items.push({id: `extra-${index}`, file: extra, locked: true})
    })
  }

  // One loader for the card, as on the Add Teachers card: the lines show no spinner of their own.
  const cardBusy: UploadStatusBusy | null =
    file && (phase === 'uploading' || importing)
      ? {
          text: importing
            ? intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.IMPORTING'})
            : uploadProgress !== null
              ? intl.formatMessage(
                  {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_UPLOADING'},
                  {percent: Math.round(uploadProgress * 100)}
                )
              : intl.formatMessage({
                  id:
                    extraFiles.length > 0
                      ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_EXTRACTING_FILES'
                      : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_EXTRACTING',
                }),
          progress: !importing && phase === 'uploading' ? uploadProgress : null,
          // A photo or scan is read by AI, page by page: say it takes a while.
          hint:
            !importing && uploadProgress === null && !isSpreadsheetUpload(file)
              ? intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.READING_FILE_SLOW'})
              : null,
        }
      : null

  // --- Map columns: one content, shown on the page (`inlineMapper`) or in the earlier dialog ---
  const mapperShown = Boolean(file) && mapperOpen && (needsMapping || inlineImport)
  const mapperStepLabel =
    phase === 'review'
      ? intl.formatMessage({id: 'CSV_IMPORT.STEPS.REVIEW_IMPORT'})
      : intl.formatMessage({id: 'CSV_IMPORT.STEPS.MAP_COLUMNS'})
  const backButtonClass = inlineMapper
    ? 'ta-btn ta-btn--outline me-auto'
    : 'onboarding-btn-back me-auto'
  const nextButtonClass = inlineMapper ? 'ta-btn ta-btn--primary' : 'onboarding-btn-next'

  const mapperBody = (
    <>
      {phase === 'sheets' && (
        <div className='d-flex flex-column gap-2'>
          <p style={{color: textColor, fontWeight: 600}}>
            {intl.formatMessage({id: 'CSV_IMPORT.STEPS.UPLOAD_FILE'})}
          </p>
          {spreadsheetTabs.map((tab) => (
            <button
              key={tab}
              type='button'
              className={
                inlineMapper
                  ? 'ta-btn ta-btn--ghost justify-content-start'
                  : 'btn btn-light-primary text-start'
              }
              onClick={() => {
                setSelectedTab(tab)
                if (file) uploadFile(file, tab)
              }}
            >
              <Upload size={14} className='me-2' aria-hidden />
              {tab}
            </button>
          ))}
        </div>
      )}

      {(phase === 'map' || phase === 'review') && showReadinessPanel && (
        <ImportReadinessPanel importReadiness={importReadiness} className='mb-4' />
      )}

      {phase === 'map' && !blocked && autoMapped && renderAutoMappedSummary()}

      {phase === 'map' && !blocked && !autoMapped && (
        <CsvColumnMapper
          csvColumns={csvColumns}
          columnMappings={columnMappings}
          columnMatches={columnMatches}
          schemas={schemas}
          selectedTable={selectedTable}
          tableMappings={tableMappings}
          warnings={warnings}
          foreignKeyOptions={foreignKeyOptions}
          compactSingleBranch={inlineMapper}
          onColumnMappingChange={(csvColumn, dbColumn) =>
            setColumnMappings((prev) => ({...prev, [csvColumn]: dbColumn}))
          }
          onDefaultValuesChange={setDefaultValues}
          onHasUnresolvedColumns={setHasUnresolvedColumns}
        />
      )}

      {phase === 'review' && (
        <CsvImportReview
          file={file}
          preview={preview}
          tableMappings={tableMappings}
          columnMappings={columnMappings}
          warnings={warnings}
          foreignKeyOptions={foreignKeyOptions}
          defaultValues={defaultValues}
          onDefaultValuesChange={setDefaultValues}
          // The allocation rows bulk-import actually receives, not the file's preview rows.
          recordCount={allocationCount ?? undefined}
        />
      )}
    </>
  )

  const importSpinner = importing ? (
    <span className='spinner-border spinner-border-sm me-2' role='status' />
  ) : null
  const mapperActions: ReactNode[] = []
  if (phase === 'review') {
    mapperActions.push(
      <button
        key='back'
        type='button'
        className={backButtonClass}
        disabled={importing}
        onClick={() => setPhase('map')}
      >
        <ArrowLeft size={16} className='me-2' />
        {intl.formatMessage({id: 'CSV_IMPORT.BUTTON.BACK'})}
      </button>
    )
  }
  // Our template: imported from here (the server built it; no review step).
  if (phase === 'map' && isInstituteExcelFlow) {
    mapperActions.push(
      <button
        key='import'
        type='button'
        className={nextButtonClass}
        disabled={isImportDisabled}
        onClick={handleImport}
      >
        {importSpinner}
        {importLabel}
      </button>
    )
  }
  if (phase === 'map' && !isInstituteExcelFlow) {
    mapperActions.push(
      <button
        key='next'
        type='button'
        className={nextButtonClass}
        disabled={isNextDisabled}
        onClick={() => setPhase('review')}
      >
        {intl.formatMessage({id: 'CSV_IMPORT.BUTTON.NEXT'})}
        <ArrowRight size={16} className='ms-2' />
      </button>
    )
  }
  if (phase === 'review') {
    mapperActions.push(
      <button
        key='import'
        type='button'
        className={nextButtonClass}
        disabled={isImportDisabled}
        onClick={handleImport}
      >
        {importSpinner}
        {intl.formatMessage({id: 'CSV_IMPORT.BUTTON.IMPORT_DATA'})}
      </button>
    )
  }

  return (
    <>
      <UploadStatusPanel
        items={items}
        busy={cardBusy}
        onRemove={(id) => {
          if (id === 'imported') {
            onClearLastImport()
            return
          }
          if (importing) return
          resetState()
          onClose()
        }}
      >
        {file && phase === 'failed' && conversion && (
          <DataUploadReport conversion={conversion} isDark={isDark} />
        )}

        {/* Blocked by new teachers: the file's line already says so in one sentence. */}
        {inlineImport && showReadinessPanel && !blockingTeachers && (
          <ImportReadinessPanel importReadiness={importReadiness} />
        )}
        {inlineImport && conversion && <DataUploadReport conversion={conversion} isDark={isDark} />}
      </UploadStatusPanel>

      {/* Map columns, for every file: our template (or a converted file) shows how it was
          mapped and imports from here; a spreadsheet in another layout is matched by hand, then
          reviewed and imported. Closing it keeps the file; its line reopens it.
          `inlineMapper` (redesigned page): a card under the file's line. Otherwise the earlier
          dialog — no bg-dark there: Metronic's dark theme INVERTS the `dark` semantic color
          ($dark-dark: #f9f9f9), so bg-dark painted the modal light exactly in dark mode. */}
      {inlineMapper ? (
        mapperShown && (
          <section className='ta-card ta-map-panel' aria-label={mapperStepLabel}>
            <header className='ta-map-panel__head'>
              <div className='ta-map-panel__titles'>
                <h2 className='ta-map-panel__title'>
                  {intl.formatMessage({id: 'CSV_IMPORT.UPLOAD.TITLE'})}
                </h2>
                <span className='ta-map-panel__step'>{mapperStepLabel}</span>
              </div>
              {!importing && (
                <button
                  type='button'
                  className='ta-icon-btn'
                  aria-label={intl.formatMessage({id: 'GENERAL.CLOSE'})}
                  onClick={() => setMapperOpen(false)}
                >
                  <X size={18} aria-hidden />
                </button>
              )}
            </header>
            <div className='ta-map-panel__body'>{mapperBody}</div>
            {mapperActions.length > 0 && (
              <footer className='ta-map-panel__foot'>{mapperActions}</footer>
            )}
          </section>
        )
      ) : (
        <Modal
          show={mapperShown}
          onHide={importing ? () => {} : () => setMapperOpen(false)}
          size='xl'
          centered
          backdrop='static'
        >
          <Modal.Header closeButton={!importing}>
            <Modal.Title style={{color: textColor}}>
              {intl.formatMessage({id: 'CSV_IMPORT.UPLOAD.TITLE'})}
              <span
                className='ms-3'
                style={{color: mutedColor, fontSize: '0.85rem', fontWeight: 500}}
              >
                {mapperStepLabel}
              </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>{mapperBody}</Modal.Body>
          <Modal.Footer>{mapperActions}</Modal.Footer>
        </Modal>
      )}

      {/* The last import's full answer (per-table rows, errors, missing references), on
          request from its line. */}
      <ImportResultModal
        show={detailsOpen && Boolean(lastImport)}
        onHide={() => setDetailsOpen(false)}
        result={lastImport?.result}
        onClose={() => setDetailsOpen(false)}
        disableDoneNavigation
      />

      {/* The people the file names that the school has not saved, in one dialog: teachers
          (save and assign, save only, or not) and students (save only, save and choose their
          classes and subjects on the Students step, or not). */}
      <NewPeopleModal
        show={Boolean(file) && Boolean(newPeopleQuestion)}
        teachers={newPeopleQuestion?.teachers ?? NO_NEW_TEACHERS}
        canContinueWithout={newPeopleQuestion?.canContinueWithout ?? false}
        // "Grade 1 A / Urdu": the file gives class, section and subject — allocated as written.
        // A line with less ("Urdu"): its classes and sections are asked on the re-read.
        assignScope={
          (newPeopleQuestion?.teachers ?? []).every(
            (teacher) =>
              teacher.samples.length > 0 &&
              teacher.samples.every((sample) => sample.includes(' / '))
          )
            ? 'lines'
            : 'classes'
        }
        // Teachers only here: the file's students are asked about once its allocations are in.
        students={NO_NEW_STUDENTS}
        submitting={savingNewTeachers}
        onCancel={() => answerNewPeople({teachers: 'none', teacherKeys: [], students: 'none'})}
        onConfirm={answerNewPeople}
      />

      {/* NEEDS_ASSIGNMENT: whose list each sheet is. Cancelling leaves the file unread. */}
      <SheetAssignmentModal
        show={Boolean(file) && Boolean(sheetQuestion)}
        question={sheetQuestion ? sheetChoices(sheetQuestion.question) : null}
        files={sheetQuestion ? [sheetQuestion.file] : []}
        onCancel={() => {
          setSheetQuestion(null)
          onClose()
        }}
        onConfirm={(assignments) => {
          const pending = sheetQuestion
          if (!pending) return
          setSheetQuestion(null)
          rememberAnswers(pending.file, {sheets: assignments})
          uploadFile(pending.file, pending.sheet)
        }}
      />

      {/* BRANCH_REQUIRED: which branch the allocations are in (wording from `rowNoun`).
          ALLOCATION_CLASSES_REQUIRED: which classes lines with no clear class are in. Skip keeps
          the file: those lines are left out and the rest is imported. Only a branch question
          left alone (nothing imports without it) drops the file. */}
      <ColumnAssignmentModal
        show={Boolean(file) && Boolean(branchQuestion)}
        columns={null}
        branches={branchQuestion?.questions ?? NO_BRANCH_QUESTIONS}
        allocations={branchQuestion?.allocations ?? NO_ALLOCATION_QUESTIONS}
        newTeachers={branchQuestion?.teachers ?? NO_NEW_TEACHERS}
        subjects={null}
        files={branchQuestion ? [branchQuestion.file] : []}
        submitting={savingNewTeachers}
        onSkip={() => {
          const pending = branchQuestion
          setBranchQuestion(null)
          // "Which classes?" skipped: each asked line answered with no class, so the server
          // leaves those out and imports the rest of the file (posted once more).
          const skipped = pending ? skippedAllocationAnswers(pending.allocations) : []
          if (!pending || skipped.length === 0) {
            onClose()
            return
          }
          rememberAnswers(pending.file, {
            allocations: skipped,
            // Not saved: skipped with the rest.
            declinedNewTeachers: pending.teachers.map((teacher) => teacher.key),
          })
          uploadFile(pending.file, pending.sheet)
        }}
        onConfirm={(answers) => {
          const pending = branchQuestion
          if (!pending) return
          const approved = answers.newTeachers
          // Every answer, then ONE re-post of the same file.
          const repost = () => {
            setBranchQuestion(null)
            rememberAnswers(pending.file, {
              branches: answers.branches,
              columns: answers.columns,
              allocations: answers.allocations,
              approvedNewTeachers: approved,
              declinedNewTeachers: pending.teachers
                .map((teacher) => teacher.key)
                .filter((key) => !approved.includes(key)),
            })
            uploadFile(pending.file, pending.sheet)
          }
          if (approved.length === 0) {
            repost()
            return
          }
          // The ticked teachers are saved first (the wizard's teacher save, as from the
          // new-teachers popup), so the re-post matches their lines.
          setSavingNewTeachers(true)
          onSaveNewTeachers(pending.teachers.filter((teacher) => approved.includes(teacher.key)))
            .then(() => {
              setSavingNewTeachers(false)
              repost()
            })
            // The save said why; the dialog stays open to try again.
            .catch(() => setSavingNewTeachers(false))
        }}
      />
    </>
  )
}

export default AllocationFileImport
